import { Box, Text } from '@chakra-ui/react';
import { useState, useCallback, useEffect } from 'react';

const PillSelector = ({
    values = [],
    onInsert,
    inputRefs = {},
    darkMode = false,
    description = 'Click the below values to dynamically populate the field.',
    borderColor,
    backgroundColor,
}) => {
    const [activeFieldName, setActiveFieldName] = useState(null);

    const setupFocusListeners = useCallback(() => {
        Object.entries(inputRefs).forEach(([fieldName, ref]) => {
            if (ref?.current) {
                const element = ref.current;

                const handleFocus = () => {
                    setActiveFieldName(fieldName);
                };

                element.addEventListener('focus', handleFocus);
            }
        });
    }, [inputRefs]);

    useEffect(() => {
        setupFocusListeners();
    }, [setupFocusListeners]);

    const handlePillClick = (value) => {
        if (!activeFieldName || !inputRefs[activeFieldName]?.current) {
            return;
        }

        const inputElement = inputRefs[activeFieldName].current;
        const currentValue = inputElement.value || '';

        const start = inputElement.selectionStart || 0;
        const end = inputElement.selectionEnd || 0;

        const newValue =
            currentValue.substring(0, start) +
            value +
            currentValue.substring(end);

        // Check if input has maxLength and respect it
        const maxLength =
            inputElement.getAttribute('maxLength') || inputElement.maxLength;
        if (maxLength && newValue.length > parseInt(maxLength, 10)) {
            return;
        }

        inputElement.value = newValue;

        const event = new Event('input', { bubbles: true });
        inputElement.dispatchEvent(event);

        const newCursorPos = start + value.length;
        inputElement.selectionStart = newCursorPos;
        inputElement.selectionEnd = newCursorPos;

        inputElement.focus();

        if (onInsert) {
            onInsert(newValue, activeFieldName);
        }
    };

    return (
        <>
            {description && (
                <Text fontSize="xs" mb={2} color="#757575" paddingTop={2}>
                    {description}
                </Text>
            )}
            <Box
                mt={2}
                p={3}
                border="1px solid"
                borderColor={
                    borderColor || (darkMode ? 'gray.600' : 'gray.300')
                }
                borderRadius="md"
                bg={backgroundColor}>
                <Box display="flex" flexWrap="wrap" gap={2}>
                    {values.map((value, index) => {
                        const displayValue =
                            value.length > 64
                                ? value.substring(0, 64) + '...'
                                : value;

                        return (
                            <Box
                                key={index}
                                as="button"
                                type="button"
                                px={3}
                                py={1}
                                bg={darkMode ? 'gray.700' : 'gray.50'}
                                border="1px solid"
                                borderColor={darkMode ? 'gray.600' : 'gray.300'}
                                borderRadius="md"
                                fontSize="sm"
                                cursor="pointer"
                                _hover={{
                                    bg: darkMode ? 'gray.600' : 'gray.200',
                                }}
                                onClick={() => handlePillClick(value)}
                                title={value.length > 64 ? value : undefined}>
                                {displayValue}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </>
    );
};

export default PillSelector;
